import type { CustomField, CustomSection } from '@gcv/shared';

import { isScoredQuestion, REQUIRED_WEIGHT_TOTAL } from 'util/risk-assessment.util';

export type SectionWeightState = 'balanced' | 'under' | 'over';

/** The API rejects weights outside this range. */
export const MIN_WEIGHT = 0;
export const MAX_WEIGHT = 100;

/**
 * Clamp a typed weight entry to a whole number in range. Blank or unparseable reads as "unset",
 * which is how the builder clears a weight.
 */
export const parseWeightEntry = (entry: string): number | undefined => {
  const trimmed = entry.trim();

  if (trimmed === '') {
    return undefined;
  }

  const parsed = Number(trimmed);

  if (!Number.isFinite(parsed)) {
    return undefined;
  }

  return Math.min(Math.max(Math.round(parsed), MIN_WEIGHT), MAX_WEIGHT);
};

export interface SectionWeightSummary {
  /** Questions that contribute to the score, in field order. */
  scoredFieldIds: string[];
  /** Each scored question's weight, in field order — the addends of the total. */
  addends: number[];
  total: number;
  state: SectionWeightState;
  /** How far off 100 the total is; 0 when balanced. */
  difference: number;
}

/** Whether this field contributes to its section's weight total. */
export const isWeightedQuestion = (field: CustomField): boolean => isScoredQuestion(field);

/**
 * A section's question-weight total broken into its addends, with the state the validation
 * container renders. Uses the same scored-question rule as the publish gate so the two agree.
 */
export const getSectionWeightSummary = (section: CustomSection): SectionWeightSummary => {
  const scored = (section?.fields ?? []).filter(isWeightedQuestion);
  const addends = scored.map((field) => (field as { weight?: number }).weight ?? 0);
  const total = addends.reduce((sum, weight) => sum + weight, 0);

  let state: SectionWeightState = 'balanced';
  if (total < REQUIRED_WEIGHT_TOTAL) {
    state = 'under';
  } else if (total > REQUIRED_WEIGHT_TOTAL) {
    state = 'over';
  }

  return {
    scoredFieldIds: scored.map((field) => field.id),
    addends,
    total,
    state,
    difference: Math.abs(REQUIRED_WEIGHT_TOTAL - total)
  };
};
